// src/components/charts/CostesCharts.jsx
import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import DonutChart from '../agComponents/DonutChart';
import BarrasHorizontalesChart from './BarrasHorizontalesChart';
import kpiService from '../../services/kpiService';
import { formatCurrency } from '../../utils/formatters';

/**
 * Componente que agrupa los gráficos de Costes (Imagen 3)
 * - Grupos de costes (barras horizontales - izquierda)
 * - Distribución de los costes (dona - centro)
 * - Principales subgrupos de costes (barras horizontales - derecha)
 */
const CostesCharts = ({ fechaInicio, fechaFin, empresaId }) => {
  const [gruposData, setGruposData] = React.useState(null);
  const [distribucionData, setDistribucionData] = React.useState(null);
  const [subgruposData, setSubgruposData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Obtener grupos de costes
        const gruposResponse = await kpiService.getGruposCostes({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        // Obtener distribución de costes
        const distribucionResponse = await kpiService.getDistribucionCostes({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        // Obtener subgrupos de costes
        const subgruposResponse = await kpiService.getSubgruposCostes({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        if (gruposResponse.success && gruposResponse.data) {
          setGruposData(gruposResponse.data);
        }

        if (distribucionResponse.success && distribucionResponse.data) {
  // mockDistribucionCostes ya es { data: [...] }
  setDistribucionData(distribucionResponse.data);
}

        if (subgruposResponse.success && subgruposResponse.data) {
          setSubgruposData(subgruposResponse.data);
        }
      } catch (err) {
        console.error('Error fetching costes data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fechaInicio, fechaFin, empresaId]);

  if (loading) {
    return (
      <Row className="g-3 mb-4">
        <Col xs={12} className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando datos...</p>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="g-3 mb-4">
      {/* Grupos de costes - Barras con valores */}
      <Col lg={4}>
        {gruposData && Array.isArray(gruposData) && gruposData.length > 0 ? (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Grupos de costes</h5>
              <div className="d-flex flex-column gap-4">
                {gruposData.map((grupo, idx) => (
                  <div key={idx}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="small fw-medium">{grupo.grupo || 'Sin nombre'}</span>
                      <span className="fw-semibold">{formatCurrency(grupo.valor || 0)}</span>
                    </div>
                    <div className="position-relative" style={{ height: '24px' }}>
                      <div
                        className="position-absolute w-100 rounded"
                        style={{
                          height: '100%',
                          backgroundColor: '#e5e7eb',
                        }}
                      />
                      <div
                        className="position-absolute rounded"
                        style={{
                          height: '100%',
                          width: '75%',
                          backgroundColor: grupo.color || '#3b82f6',
                          transition: 'width 0.6s ease-out',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Grupos de costes</h5>
              <p className="text-muted text-center py-4">No hay datos disponibles</p>
            </Card.Body>
          </Card>
        )}
      </Col>

      {/* Distribución de los costes - Dona */}
      <Col lg={4}>
{distribucionData?.data && Array.isArray(distribucionData.data) && distribucionData.data.length > 0 ? (          <Card>
            <Card.Body>
              <h5 className="mb-4">Distribución de los costes</h5>
              <div className="text-center mb-3">
                <small className="text-muted">Áreas</small>
              </div>
              <DonutChart
                data={distribucionData.data}
                angleKey="value"
                labelKey="label"
                height={300}
                innerRadiusRatio={0.65}
              />
              {/* Leyenda con porcentajes */}
              <div className="mt-3">
                {distribucionData.data.map((item, idx) => (
                  <div key={idx} className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: item.color || '#3b82f6',
                        }}
                      />
                      <small>{item.label || 'Sin nombre'}</small>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge" style={{ backgroundColor: '#10b981' }}>
                        + {item.value || 0}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Distribución de los costes</h5>
              <p className="text-muted text-center py-4">No hay datos disponibles</p>
            </Card.Body>
          </Card>
        )}
      </Col>

      {/* Principales subgrupos de costes */}
      <Col lg={4}>
        {subgruposData && Array.isArray(subgruposData) && subgruposData.length > 0 ? (
    <BarrasHorizontalesChart
      title="Principales subgrupos de costes"
      data={subgruposData.map(item => ({
        area: item.subgrupo,
        porcentaje: item.porcentaje,
        color: item.color
      }))}
      showPercentageLabel={true}
    />
  ) : (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Principales subgrupos de costes</h5>
              <p className="text-muted text-center py-4">No hay datos disponibles</p>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
};

export default CostesCharts;